import { useTranslation } from '@presentation/utils';
import type { PropsWithChildren } from 'react';
import { useCallback, useMemo } from 'react';
import type { InputSelectProps, SelectOption } from '..';
import { DropdownSelectSearch, InputFieldClearButton } from '..';
import { Input } from '../Input';

export * from './types';

export const InputSelect = <TValue,>(props: PropsWithChildren<InputSelectProps<TValue>>) => {
  const { translate } = useTranslation();
  const {
    selectOptions,
    search,
    onSearchChange,
    onSelect,
    value = '',
    iconRight,
    placeholder = translate('common.inputs.choose.placeholder'),
    loading = false,
    ...rest
  } = props;

  const fieldValue = useMemo((): string => {
    if (search != null) return search;
    if (value) {
      const found = (selectOptions ?? []).find((option) => option.value === value);
      if (typeof found?.label === 'string') return found.label;
    }
    return '';
  }, [search, selectOptions, value]);

  const dropdownSelectHandler = useCallback(
    (selected: SelectOption<TValue>) => {
      onSelect?.(selected);
      if (document.activeElement) {
        (document.activeElement as any).blur();
      }
    },
    [onSelect],
  );

  return (
    <Input
      mobileStyleDropdown={false}
      placeholder={placeholder}
      value={fieldValue}
      onChange={(event) => {
        onSearchChange?.(event.target.value);
      }}
      iconRight={
        <InputFieldClearButton<string>
          showOnNotEmpty
          value={fieldValue}
          onClear={() => {
            if (rest.disabled) return;
            onSelect?.(undefined);
            onSearchChange?.(undefined);
          }}
        >
          {iconRight}
        </InputFieldClearButton>
      }
      {...rest}
      dropdown={
        rest.disabled ? undefined : (
          <DropdownSelectSearch<TValue>
            loading={loading}
            hideSearchInput
            onSelect={dropdownSelectHandler}
            options={selectOptions}
          />
        )
      }
    />
  );
};
